import React, { useState, useEffect } from 'react';
import { FingerType } from '@/types';
import {
  WordTrainingData,
  AdminTrainingSelection,
  HandType,
  ComplexityMode
} from '@/types/wordTraining';
import {
  getWordsByFingerCombination,
  saveCourseTrainingConfiguration,
  formatFingerCombinationForDisplay
} from '@/services/wordTrainingService';
import {
  mapFingersToKey,
  getHandFromFingersForWordTraining
} from '@/utils/fingerCombinationMapper';
import FingerToggle from '@/components/UI/FingerToggle';
import HandToggle from '@/components/UI/HandToggle';
import ComplexityToggle from '@/components/UI/ComplexityToggle';

interface ConfigurationTabProps {
  onConfigurationSaved?: (levelNumber: number, dayNumber: number, roundNumber: number) => void;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
}

type HandMode = 'left' | 'right' | 'both';

const ConfigurationTab: React.FC<ConfigurationTabProps> = ({
  onConfigurationSaved,
  onError,
  onSuccess
}) => {
  const [levelNumber, setLevelNumber] = useState<number>(1);
  const [dayNumber, setDayNumber] = useState<number>(1);
  const [roundNumber, setRoundNumber] = useState<number>(1);
  const [selectedFingers, setSelectedFingers] = useState<FingerType[]>(['leftPinky', 'rightPinky']);
  const [handMode, setHandMode] = useState<HandMode>('both');
  const [complexity, setComplexity] = useState<ComplexityMode>('words');

  // Training selection state - unified for all types
  const [trainingSelection, setTrainingSelection] = useState<AdminTrainingSelection>({
    mode: 'custom', // Always use custom mode since we removed training mode selection
    letterTraining: {
      duration: 120000, // 2 minutes default
      sequenceCount: 100
      // customKeys removed to avoid undefined values
    },
    wordTraining: {
      random: {
        wordLengths: [3, 4, 5],
        includePhrases: true,
        quantity: 20
      }
    }
  });

  // Word training state (only for words complexity)
  const [availableWordData, setAvailableWordData] = useState<WordTrainingData | null>(null);

  // UI state
  const [loadingWords, setLoadingWords] = useState(false);
  const [saving, setSaving] = useState(false);

  const getHandTypeForWordTraining = (): HandType => {
    if (handMode === 'both') {
      return getHandFromFingersForWordTraining(selectedFingers);
    }
    return handMode as HandType;
  };

  // Load available words only when complexity is 'words'
  useEffect(() => {
    if (complexity === 'words') {
      loadAvailableWords();
    } else {
      setAvailableWordData(null);
    }
  }, [selectedFingers, handMode, complexity]);

  const loadAvailableWords = async () => {
    setLoadingWords(true);
    try {
      const hand = getHandTypeForWordTraining();
      const wordData = await getWordsByFingerCombination(selectedFingers, hand);
      setAvailableWordData(wordData);

      if (!wordData) {
        onError?.(`No word training data found for selected finger combination: ${mapFingersToKey(selectedFingers)}_${hand}`);
      }
    } catch (error) {
      console.error('Error loading word data:', error);
      onError?.('Failed to load word training data');
    } finally {
      setLoadingWords(false);
    }
  };

  const handleSaveConfiguration = async () => {
    setSaving(true);
    try {
      const hand = getHandTypeForWordTraining();

      // Create finger combination based on available data or current selection
      let fingerCombination: string[];
      if (complexity === 'words' && availableWordData) {
        fingerCombination = availableWordData.fingerCombination;
      } else {
        // For letter training, derive from selected fingers
        fingerCombination = mapFingersToKey(selectedFingers).split('_');
      }

      // Clean the training selection to remove undefined values for Firebase
      const cleanTrainingSelection = cleanObjectForFirebase(trainingSelection);

      await saveCourseTrainingConfiguration(
        levelNumber,
        dayNumber,
        roundNumber,
        fingerCombination,
        hand,
        complexity,
        cleanTrainingSelection
      );

      onSuccess?.(`Configuration saved for Level ${levelNumber}, Day ${dayNumber}, Round ${roundNumber} (${complexity === 'words' ? 'Words' : `Letters 1-${complexity}`})`);
      onConfigurationSaved?.(levelNumber, dayNumber, roundNumber);
    } catch (error) {
      console.error('Error saving configuration:', error);
      onError?.('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to clean objects for Firebase (remove undefined values)
  const cleanObjectForFirebase = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return null;
    }

    if (Array.isArray(obj)) {
      return obj.map(cleanObjectForFirebase);
    }

    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = cleanObjectForFirebase(value);
        }
      }
      return cleaned;
    }

    return obj;
  };



  const renderLetterTrainingInterface = () => (
    <div className="letter-training-section">
      <div className="section-header">
        <h4>📝 Letter Training Configuration</h4>
        <div className="complexity-display">
          Complexity: Letters 1-{complexity}
        </div>
      </div>


      <div className="custom-letter-options">
        <div className="option-group">
          <label>Duration (minutes):</label>
          <input
            type="number"
            min="1"
            max="10"
            value={Math.round((trainingSelection.letterTraining?.duration || 120000) / 60000)}
            onChange={(e) => {
              const minutes = Math.max(1, Math.min(10, Number(e.target.value)));
              setTrainingSelection(prev => ({
                ...prev,
                letterTraining: {
                  ...prev.letterTraining!,
                  duration: minutes * 60000
                }
              }));
            }}
            className="duration-input"
          />
          <span className="input-note">Standard: 2 minutes</span>
        </div>

        <div className="option-group">
          <label>Sequence Count:</label>
          <input
            type="number"
            min="50"
            max="500"
            value={trainingSelection.letterTraining?.sequenceCount || 100}
            onChange={(e) => {
              const count = Math.max(50, Math.min(500, Number(e.target.value)));
              setTrainingSelection(prev => ({
                ...prev,
                letterTraining: {
                  ...prev.letterTraining!,
                  sequenceCount: count
                }
              }));
            }}
            className="count-input"
          />
          <span className="input-note">Number of training sequences</span>
        </div>
      </div>

      <div className="finger-summary">
        <h5>Selected Configuration:</h5>
        <div className="config-display">
          <div className="config-item">
            <span className="label">Fingers:</span>
            <span className="value">{formatFingerCombinationForDisplay(mapFingersToKey(selectedFingers).split('_'))}</span>
          </div>
          <div className="config-item">
            <span className="label">Hand:</span>
            <span className="value">{getHandTypeForWordTraining()}</span>
          </div>
          <div className="config-item">
            <span className="label">Mode:</span>
            <span className="value">Custom Training</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderWordTrainingInterface = () => {
    if (loadingWords) {
      return (
        <div className="loading-state">
          <div className="spinner"></div>
          <span>Loading available words...</span>
        </div>
      );
    }

    if (!availableWordData) {
      return (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>No word training data available for this finger combination.</span>
          <button onClick={loadAvailableWords} className="retry-btn">
            🔄 Retry
          </button>
        </div>
      );
    }

    const allWords = {
      length3: availableWordData.words.length3 || [],
      length4: availableWordData.words.length4 || [],
      length5: availableWordData.words.length5 || [],
      length6: availableWordData.words.length6 || [],
      length7: availableWordData.words.length7 || [],
      length8plus: availableWordData.words.length8plus || []
    };
    const totalWords = Object.values(allWords).flat().length;
    const totalPhrases = availableWordData.phrases.length;

    return (
      <div className="word-training-section">
        <div className="section-header">
          <h4>📝 Word Training Configuration</h4>
          <div className="word-stats">
            <span className="stat">{totalWords} words</span>
            <span className="stat">{totalPhrases} phrases</span>
            <span className="stat">
              {formatFingerCombinationForDisplay(availableWordData.fingerCombination)} ({availableWordData.hand})
            </span>
          </div>
        </div>


        <div className="custom-word-options">
          <div className="selection-mode">
            <label>Selection Type:</label>
            <div className="mode-buttons">
              <button
                className={`mode-btn ${trainingSelection.wordTraining?.manual ? 'active' : ''}`}
                onClick={() => setTrainingSelection(prev => ({
                  ...prev,
                  wordTraining: {
                    manual: { selectedWords: [], selectedPhrases: [] }
                  }
                }))}
              >
                📝 Manual Selection
              </button>
              <button
                className={`mode-btn ${trainingSelection.wordTraining?.random ? 'active' : ''}`}
                onClick={() => setTrainingSelection(prev => ({
                  ...prev,
                  wordTraining: {
                    random: { wordLengths: [3, 4, 5], includePhrases: true, quantity: 20 }
                  }
                }))}
              >
                🎲 Random Selection
              </button>
            </div>
          </div>

          {trainingSelection.wordTraining?.random && (
            <div className="random-selection">
              <div className="option-group">
                <label>Word Lengths:</label>
                <div className="checkbox-grid">
                  {([3, 4, 5, 6, 7, 8] as const).map(length => {
                    const count = length === 8 ? allWords.length8plus?.length || 0 :
                      allWords[`length${length}` as keyof typeof allWords]?.length || 0;
                    return (
                      <label key={length} className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={trainingSelection.wordTraining?.random?.wordLengths.includes(length) || false}
                          onChange={() => {
                            if (trainingSelection.wordTraining?.random) {
                              const currentLengths = trainingSelection.wordTraining.random.wordLengths;
                              const newLengths = currentLengths.includes(length)
                                ? currentLengths.filter(l => l !== length)
                                : [...currentLengths, length];

                              setTrainingSelection(prev => ({
                                ...prev,
                                wordTraining: {
                                  random: {
                                    ...prev.wordTraining!.random!,
                                    wordLengths: newLengths
                                  }
                                }
                              }));
                            }
                          }}
                        />
                        <span>{length === 8 ? '8+' : length} letters ({count})</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="option-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={trainingSelection.wordTraining?.random?.includePhrases || false}
                    onChange={() => {
                      if (trainingSelection.wordTraining?.random) {
                        setTrainingSelection(prev => ({
                          ...prev,
                          wordTraining: {
                            random: {
                              ...prev.wordTraining!.random!,
                              includePhrases: !prev.wordTraining!.random!.includePhrases
                            }
                          }
                        }));
                      }
                    }}
                  />
                  <span>📝 Include Phrases ({totalPhrases})</span>
                </label>
              </div>

              <div className="option-group">
                <label>Quantity:</label>
                <div className="quantity-controls">
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={trainingSelection.wordTraining?.random?.quantity || 20}
                    onChange={(e) => {
                      if (trainingSelection.wordTraining?.random) {
                        const quantity = Math.max(1, Math.min(100, Number(e.target.value)));
                        setTrainingSelection(prev => ({
                          ...prev,
                          wordTraining: {
                            random: {
                              ...prev.wordTraining!.random!,
                              quantity
                            }
                          }
                        }));
                      }
                    }}
                    className="quantity-input"
                  />
                  <span className="quantity-label">items</span>
                </div>
              </div>
            </div>
          )}

          {trainingSelection.wordTraining?.manual && (
            <div className="manual-selection">
              <h5>📝 Manual Word Selection</h5>
              <p className="selection-note">
                Select specific words and phrases for focused training. Selected items will be repeated throughout the session.
              </p>

              {/* Individual Words Selection */}
              <div className="words-section">
                <h6>🎯 Individual Words ({totalWords} available)</h6>
                <div className="word-categories">
                  {Object.entries(allWords).map(([category, words]) => {
                    const length = category.replace('length', '').replace('plus', '+');
                    if (words.length === 0) return null;

                    return (
                      <div key={category} className="word-category">
                        <div className="category-header">
                          <span className="category-title">{length} letters ({words.length} words)</span>
                          <div className="category-controls">
                            <button
                              className="select-all-btn"
                              onClick={() => {
                                const currentSelected = trainingSelection.wordTraining?.manual?.selectedWords || [];
                                const allCategorySelected = words.every(word => currentSelected.includes(word));

                                let newSelection;
                                if (allCategorySelected) {
                                  // Unselect all from this category
                                  newSelection = currentSelected.filter(word => !words.includes(word));
                                } else {
                                  // Select all from this category
                                  newSelection = [...new Set([...currentSelected, ...words])];
                                }

                                setTrainingSelection(prev => ({
                                  ...prev,
                                  wordTraining: {
                                    manual: {
                                      selectedWords: newSelection,
                                      selectedPhrases: prev.wordTraining?.manual?.selectedPhrases || []
                                    }
                                  }
                                }));
                              }}
                            >
                              {words.every(word => (trainingSelection.wordTraining?.manual?.selectedWords || []).includes(word))
                                ? '✖ Unselect All' : '✓ Select All'}
                            </button>
                          </div>
                        </div>
                        <div className="word-list">
                          {words.map(word => (
                            <label key={word} className="word-item">
                              <input
                                type="checkbox"
                                checked={(trainingSelection.wordTraining?.manual?.selectedWords || []).includes(word)}
                                onChange={() => {
                                  const currentSelected = trainingSelection.wordTraining?.manual?.selectedWords || [];
                                  const newSelection = currentSelected.includes(word)
                                    ? currentSelected.filter(w => w !== word)
                                    : [...currentSelected, word];

                                  setTrainingSelection(prev => ({
                                    ...prev,
                                    wordTraining: {
                                      manual: {
                                        selectedWords: newSelection,
                                        selectedPhrases: prev.wordTraining?.manual?.selectedPhrases || []
                                      }
                                    }
                                  }));
                                }}
                              />
                              <span className="word-text">{word}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Phrases Selection */}
              {totalPhrases > 0 && (
                <div className="phrases-section">
                  <h6>📝 Phrases ({totalPhrases} available)</h6>
                  <div className="phrases-controls">
                    <button
                      className="select-all-phrases-btn"
                      onClick={() => {
                        const currentSelected = trainingSelection.wordTraining?.manual?.selectedPhrases || [];
                        const allSelected = availableWordData!.phrases.every(phrase => currentSelected.includes(phrase));

                        let newSelection: string[];
                        if (allSelected) {
                          newSelection = [];
                        } else {
                          newSelection = [...availableWordData!.phrases];
                        }

                        setTrainingSelection(prev => ({
                          ...prev,
                          wordTraining: {
                            manual: {
                              selectedWords: prev.wordTraining?.manual?.selectedWords || [],
                              selectedPhrases: newSelection
                            }
                          }
                        }));
                      }}
                    >
                      {availableWordData!.phrases.every(phrase => (trainingSelection.wordTraining?.manual?.selectedPhrases || []).includes(phrase))
                        ? '✖ Unselect All Phrases' : '✓ Select All Phrases'}
                    </button>
                  </div>
                  <div className="phrase-list">
                    {availableWordData!.phrases.map(phrase => (
                      <label key={phrase} className="phrase-item">
                        <input
                          type="checkbox"
                          checked={(trainingSelection.wordTraining?.manual?.selectedPhrases || []).includes(phrase)}
                          onChange={() => {
                            const currentSelected = trainingSelection.wordTraining?.manual?.selectedPhrases || [];
                            const newSelection = currentSelected.includes(phrase)
                              ? currentSelected.filter(p => p !== phrase)
                              : [...currentSelected, phrase];

                            setTrainingSelection(prev => ({
                              ...prev,
                              wordTraining: {
                                manual: {
                                  selectedWords: prev.wordTraining?.manual?.selectedWords || [],
                                  selectedPhrases: newSelection
                                }
                              }
                            }));
                          }}
                        />
                        <span className="phrase-text">{phrase}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* 🆕 Randomization Control */}
              <div className="randomization-control">
                <h6>🎯 Training Behavior</h6>
                <label className="randomization-checkbox">
                  <input
                    type="checkbox"
                    checked={trainingSelection.wordTraining?.manual?.randomizeOrder || false}
                    onChange={() => {
                      setTrainingSelection(prev => ({
                        ...prev,
                        wordTraining: {
                          manual: {
                            selectedWords: prev.wordTraining?.manual?.selectedWords || [],
                            selectedPhrases: prev.wordTraining?.manual?.selectedPhrases || [],
                            randomizeOrder: !prev.wordTraining?.manual?.randomizeOrder
                          }
                        }
                      }));
                    }}
                  />
                  <div className="checkbox-content">
                    <span className="checkbox-title">🎲 Randomize Word Order</span>
                    <div className="checkbox-description">
                      {trainingSelection.wordTraining?.manual?.randomizeOrder ? (
                        <span className="behavior-desc random">📀 Words will be mixed randomly throughout the 2-minute session</span>
                      ) : (
                        <span className="behavior-desc sequential">⏱️ Words will be shown sequentially - each word gets equal time (e.g., 1 minute each for 2 words)</span>
                      )}
                    </div>
                  </div>
                </label>
              </div>

              {/* Selection Summary */}
              <div className="selection-summary">
                <h6>📋 Selection Summary</h6>
                <div className="summary-stats">
                  <div className="summary-item">
                    <span className="summary-label">Selected Words:</span>
                    <span className="summary-value">{(trainingSelection.wordTraining?.manual?.selectedWords || []).length}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Selected Phrases:</span>
                    <span className="summary-value">{(trainingSelection.wordTraining?.manual?.selectedPhrases || []).length}</span>
                  </div>
                  <div className="summary-item total">
                    <span className="summary-label">Total Training Items:</span>
                    <span className="summary-value">
                      {(trainingSelection.wordTraining?.manual?.selectedWords || []).length +
                        (trainingSelection.wordTraining?.manual?.selectedPhrases || []).length}
                    </span>
                  </div>
                  <div className="summary-item behavior">
                    <span className="summary-label">Training Mode:</span>
                    <span className="summary-value behavior-mode">
                      {trainingSelection.wordTraining?.manual?.randomizeOrder ? '🎲 Random Order' : '⏱️ Sequential Order'}
                    </span>
                  </div>
                </div>

                {((trainingSelection.wordTraining?.manual?.selectedWords || []).length +
                  (trainingSelection.wordTraining?.manual?.selectedPhrases || []).length) === 0 && (
                    <div className="empty-selection-warning">
                      ⚠️ Please select at least one word or phrase for training.
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTrainingInterface = () => {
    if (complexity === 'words') {
      return renderWordTrainingInterface();
    } else {
      return renderLetterTrainingInterface();
    }
  };

  const canSave = () => {
    if (complexity === 'words') {
      if (!availableWordData) {
        return false;
      }

      // For manual selection, check if at least one word or phrase is selected
      if (trainingSelection.wordTraining?.manual) {
        const selectedWords = trainingSelection.wordTraining.manual.selectedWords || [];
        const selectedPhrases = trainingSelection.wordTraining.manual.selectedPhrases || [];
        return selectedWords.length > 0 || selectedPhrases.length > 0;
      }

      return true; // Random selection is always valid if word data is available
    }
    return true; // Letter training can always be saved
  };

  return (
    <div className="configuration-tab">
      <div className="config-header">
        <h3>⚙️ Course Training Configuration</h3>
        <p>Configure ANY training type for specific course days and rounds - letters or words.</p>
      </div>

      <div className="config-sections">
        <div className="section day-round-selection">
          <h4>📅 Level, Day & Round Selection</h4>
          <div className="selection-controls">
            <div className="control-group">
              <label>Level:</label>
              <select
                value={levelNumber}
                onChange={(e) => setLevelNumber(Number(e.target.value))}
                className="level-select"
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map(level => (
                  <option key={level} value={level}>Level {level}</option>
                ))}
              </select>
            </div>
            <div className="control-group">
              <label>Day:</label>
              <select
                value={dayNumber}
                onChange={(e) => setDayNumber(Number(e.target.value))}
                className="day-select"
              >
                {Array.from({ length: 30 }, (_, i) => i + 1).map(day => (
                  <option key={day} value={day}>Day {day}</option>
                ))}
              </select>
            </div>
            <div className="control-group">
              <label>Round:</label>
              <select
                value={roundNumber}
                onChange={(e) => setRoundNumber(Number(e.target.value))}
                className="round-select"
              >
                {Array.from({ length: 5 }, (_, i) => i + 1).map(round => (
                  <option key={round} value={round}>Round {round}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="section training-setup">
          <h4>🎯 Training Configuration</h4>
          <div className="training-controls">
            <div className="control-group">
              <label>Fingers:</label>
              <FingerToggle
                selectedFingers={selectedFingers}
                onChange={setSelectedFingers}
              />
            </div>
            <div className="control-group">
              <label>Hand:</label>
              <HandToggle
                value={handMode}
                onChange={setHandMode}
              />
            </div>
            <div className="control-group">
              <label>Complexity:</label>
              <ComplexityToggle
                value={complexity}
                onChange={setComplexity}
              />
            </div>
          </div>
        </div>

        {renderTrainingInterface()}

        <div className="section save-section">
          <div className="save-controls">
            <button
              onClick={handleSaveConfiguration}
              disabled={saving || !canSave()}
              className="save-btn"
            >
              {saving ? (
                <>
                  <div className="btn-spinner"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>💾</span>
                  <span>Save Configuration</span>
                </>
              )}
            </button>

            {!canSave() && complexity === 'words' && (
              <p className="save-note">
                {!availableWordData
                  ? '💡 Load word data for the selected finger combination to enable saving'
                  : trainingSelection.wordTraining?.manual
                    ? '💡 Select at least one word or phrase for manual training'
                    : '💡 Configure training options to enable saving'
                }
              </p>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .configuration-tab {
          background: rgba(31, 41, 55, 0.3);
          border-radius: 8px;
          padding: 24px;
        }

        .config-header {
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(75, 85, 99, 0.3);
        }

        .config-header h3 {
          margin: 0 0 8px 0;
          color: #3b82f6;
          font-size: 1.2rem;
        }

        .config-header p {
          margin: 0;
          color: #9ca3af;
          font-size: 0.9rem;
        }

        .config-sections {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .section {
          background: rgba(17, 24, 39, 0.5);
          border-radius: 8px;
          padding: 20px;
          border: 1px solid rgba(75, 85, 99, 0.3);
        }

        .section h4 {
          margin: 0 0 16px 0;
          color: #e5e7eb;
          font-size: 1rem;
        }

        .selection-controls {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .training-controls {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
          align-items: end;
        }

        .control-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .control-group label {
          font-size: 0.85rem;
          color: #d1d5db;
          font-weight: 600;
        }

        .day-select, .round-select {
          padding: 8px 12px;
          background: rgba(31, 41, 55, 0.8);
          border: 1px solid rgba(75, 85, 99, 0.5);
          border-radius: 6px;
          color: white;
          font-size: 0.9rem;
          min-width: 120px;
        }

        .day-select:focus, .round-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .word-stats, .complexity-display {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .stat {
          font-size: 0.8rem;
          color: #9ca3af;
          background: rgba(75, 85, 99, 0.3);
          padding: 4px 8px;
          border-radius: 4px;
        }

        .complexity-display {
          font-size: 0.9rem;
          color: #22c55e;
          font-weight: 600;
        }

        .training-mode-selection {
          margin-bottom: 20px;
        }

        .training-mode-selection label, .selection-mode label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #e5e7eb;
          font-size: 0.9rem;
        }

        .mode-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .mode-btn {
          padding: 8px 16px;
          border: 1px solid rgba(75, 85, 99, 0.5);
          background: rgba(31, 41, 55, 0.5);
          color: #9ca3af;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .mode-btn:hover {
          background: rgba(75, 85, 99, 0.3);
          color: #e5e7eb;
        }

        .mode-btn.active {
          background: rgba(59, 130, 246, 0.8);
          border-color: rgba(59, 130, 246, 0.6);
          color: white;
        }

        .custom-letter-options, .custom-word-options {
          background: rgba(75, 85, 99, 0.1);
          border-radius: 6px;
          padding: 16px;
          margin-top: 16px;
        }

        .option-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
        }

        .option-group:last-child {
          margin-bottom: 0;
        }

        .option-group > label {
          font-weight: 600;
          color: #e5e7eb;
          font-size: 0.85rem;
        }

        .duration-input, .count-input, .quantity-input {
          width: 80px;
          padding: 6px 8px;
          background: rgba(17, 24, 39, 0.8);
          border: 1px solid rgba(75, 85, 99, 0.5);
          border-radius: 4px;
          color: white;
          font-size: 0.85rem;
        }

        .duration-input:focus, .count-input:focus, .quantity-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        .input-note, .quantity-label {
          font-size: 0.8rem;
          color: #9ca3af;
          margin-top: 4px;
        }

        .finger-summary {
          margin-top: 20px;
          padding: 16px;
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 6px;
        }

        .finger-summary h5 {
          margin: 0 0 12px 0;
          color: #22c55e;
          font-size: 0.9rem;
        }

        .config-display {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .config-item {
          display: flex;
          justify-content: space-between;
        }

        .config-item .label {
          font-size: 0.8rem;
          color: #9ca3af;
          font-weight: 600;
        }

        .config-item .value {
          font-size: 0.8rem;
          color: #e5e7eb;
        }

        .checkbox-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 8px;
        }

        .checkbox-item {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 0.85rem;
          color: #e5e7eb;
        }

        .checkbox-item input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .quantity-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .manual-selection {
          padding: 20px;
          background: rgba(75, 85, 99, 0.1);
          border-radius: 6px;
          margin-top: 16px;
        }

        .manual-selection h5 {
          margin: 0 0 12px 0;
          color: #3b82f6;
          font-size: 1rem;
          font-weight: 700;
        }

        .manual-selection h6 {
          margin: 16px 0 12px 0;
          color: #e5e7eb;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .selection-note {
          margin: 0 0 20px 0;
          color: #9ca3af;
          font-size: 0.85rem;
          line-height: 1.4;
        }

        .words-section, .phrases-section {
          margin-bottom: 24px;
        }

        .word-categories {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .word-category {
          background: rgba(17, 24, 39, 0.5);
          border-radius: 6px;
          padding: 12px;
          border: 1px solid rgba(75, 85, 99, 0.3);
        }

        .category-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          flex-wrap: wrap;
          gap: 8px;
        }

        .category-title {
          font-size: 0.85rem;
          color: #d1d5db;
          font-weight: 600;
        }

        .select-all-btn, .select-all-phrases-btn {
          padding: 4px 8px;
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 4px;
          color: #60a5fa;
          cursor: pointer;
          font-size: 0.75rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .select-all-btn:hover, .select-all-phrases-btn:hover {
          background: rgba(59, 130, 246, 0.3);
          color: #93c5fd;
        }

        .word-list, .phrase-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 8px;
        }

        .phrase-list {
          grid-template-columns: 1fr;
        }

        .word-item, .phrase-item {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          padding: 6px 8px;
          background: rgba(31, 41, 55, 0.5);
          border-radius: 4px;
          border: 1px solid rgba(75, 85, 99, 0.3);
          transition: all 0.2s ease;
        }

        .word-item:hover, .phrase-item:hover {
          background: rgba(59, 130, 246, 0.1);
          border-color: rgba(59, 130, 246, 0.3);
        }

        .word-item input[type="checkbox"], .phrase-item input[type="checkbox"] {
          width: 14px;
          height: 14px;
          cursor: pointer;
        }

        .word-text, .phrase-text {
          font-size: 0.8rem;
          color: #e5e7eb;
          word-break: break-word;
        }

        .phrases-controls {
          margin-bottom: 12px;
        }

        .selection-summary {
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: 6px;
          padding: 16px;
          margin-top: 20px;
        }

        .selection-summary h6 {
          margin: 0 0 12px 0;
          color: #22c55e;
        }

        .summary-stats {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .summary-item.total {
          border-top: 1px solid rgba(34, 197, 94, 0.3);
          padding-top: 8px;
          margin-top: 4px;
          font-weight: 600;
        }

        .summary-label {
          font-size: 0.8rem;
          color: #9ca3af;
        }

        .summary-value {
          font-size: 0.8rem;
          color: #e5e7eb;
          font-weight: 600;
        }

        .summary-item.total .summary-label,
        .summary-item.total .summary-value {
          color: #22c55e;
        }

        .empty-selection-warning {
          margin-top: 12px;
          padding: 8px 12px;
          background: rgba(251, 191, 36, 0.1);
          border: 1px solid rgba(251, 191, 36, 0.3);
          border-radius: 4px;
          color: #fbbf24;
          font-size: 0.8rem;
          text-align: center;
        }

        .randomization-control {
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 6px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .randomization-control h6 {
          margin: 0 0 12px 0;
          color: #60a5fa;
          font-size: 0.9rem;
        }

        .randomization-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          cursor: pointer;
          font-size: 0.85rem;
        }

        .randomization-checkbox input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .checkbox-content {
          display: flex;
          flex-direction: column;
          gap: 6px;
          flex: 1;
        }

        .checkbox-title {
          color: #e5e7eb;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .checkbox-description {
          font-size: 0.8rem;
          line-height: 1.4;
        }

        .behavior-desc {
          display: block;
          padding: 8px 12px;
          border-radius: 4px;
          font-weight: 500;
        }

        .behavior-desc.random {
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.3);
          color: #22c55e;
        }

        .behavior-desc.sequential {
          background: rgba(168, 85, 247, 0.1);
          border: 1px solid rgba(168, 85, 247, 0.3);
          color: #a855f7;
        }

        .summary-item.behavior .behavior-mode {
          font-weight: 600;
          color: #60a5fa;
        }

        .save-controls {
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
        }

        .save-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: rgba(34, 197, 94, 0.8);
          border: none;
          border-radius: 8px;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
        }

        .save-btn:hover:not(:disabled) {
          background: rgba(34, 197, 94, 0.9);
          transform: translateY(-1px);
        }

        .save-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .btn-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .save-note {
          margin: 0;
          font-size: 0.8rem;
          color: #9ca3af;
          text-align: center;
        }

        .loading-state, .error-message {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          border-radius: 6px;
          font-size: 0.9rem;
        }

        .loading-state {
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          color: #60a5fa;
        }

        .error-message {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #fca5a5;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(59, 130, 246, 0.3);
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .retry-btn {
          padding: 4px 8px;
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 4px;
          color: #fca5a5;
          cursor: pointer;
          font-size: 0.8rem;
          margin-left: auto;
        }

        .retry-btn:hover {
          background: rgba(239, 68, 68, 0.3);
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .configuration-tab {
            padding: 16px;
          }

          .selection-controls,
          .training-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .word-stats {
            flex-direction: column;
          }

          .mode-buttons {
            flex-direction: column;
          }

          .checkbox-grid {
            grid-template-columns: 1fr;
          }

          .config-display {
            gap: 12px;
          }

          .config-item {
            flex-direction: column;
            gap: 4px;
          }
        }
      `}</style>
    </div>
  );
};

export default ConfigurationTab;